import React from 'react';
import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';
import { cx } from './cx';

/** A number tile: label in words, value in mono, one line of context. */
export const DataTile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; subTone?: 'ok' | 'bad' | 'muted'; className?: string }> = ({
  label, value, sub, subTone = 'muted', className,
}) => (
  <div className={cx('flex flex-col gap-1.5 rounded-card bg-card p-[18px]', className)}>
    <span className="text-label uppercase text-ink-3">{label}</span>
    <span className="font-money text-[28px] font-semibold leading-tight tabular text-ink">{value}</span>
    {sub && <span className={cx('text-xs', subTone === 'ok' ? 'font-semibold text-ok' : subTone === 'bad' ? 'font-semibold text-bad' : 'text-ink-2')}>{sub}</span>}
  </div>
);

/** Stage bars on top of a campaign: done (green), here (orange), working, failed, later. */
export type StageBarState = 'done' | 'current' | 'working' | 'failed' | 'scheduled' | 'next';
const BAR_CLS: Record<StageBarState, string> = {
  done: 'bg-ok', current: 'bg-brand', working: 'bg-working-stripes shadow-[inset_0_0_0_1px_rgb(var(--c-work-dot))]',
  failed: 'bg-bad', scheduled: 'bg-navy-ink', next: 'bg-line-2',
};
export const StageBars: React.FC<{ stages: { id: string; label: string; state: StageBarState; note?: string; to?: string; onClick?: () => void }[]; className?: string }> = ({ stages, className }) => (
  <nav aria-label="Stages" className={cx('flex gap-1.5', className)}>
    {stages.map(s => {
      const Tag = s.onClick ? 'button' : 'div';
      return (
        <Tag key={s.id} type={s.onClick ? 'button' : undefined} onClick={s.onClick} aria-current={s.state === 'current' ? 'step' : undefined}
          className="flex min-w-0 flex-1 flex-col gap-1.5 border-0 bg-transparent p-0 text-left">
          <span className={cx('h-1.5 rounded-full', BAR_CLS[s.state])} />
          {s.note && <span className={cx('truncate text-xs font-semibold', s.state === 'done' ? 'text-ok' : s.state === 'current' ? 'text-brand-ink' : s.state === 'failed' ? 'text-bad' : 'text-ink-3')}>{s.note}</span>}
          <span className={cx('truncate font-display text-[15px]', s.state === 'current' ? 'font-extrabold text-ink' : s.state === 'next' ? 'font-bold text-ink-2' : 'font-bold text-ink')}>{s.label}</span>
        </Tag>
      );
    })}
  </nav>
);

/** The dark "decide" bar: one line of reassurance and the one next action. */
export const DecideBar: React.FC<{ children: React.ReactNode; action: React.ReactNode; className?: string; sticky?: boolean }> = ({ children, action, className, sticky }) => (
  <div className={cx('flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-navy py-3 pl-5 pr-3 text-on-navy shadow-decide', sticky && 'sticky bottom-4 z-10', className)}>
    <div className="min-w-0 text-sm text-on-navy-2">{children}</div>
    <div className="flex flex-wrap items-center gap-2">{action}</div>
  </div>
);

export const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div aria-hidden="true" className={cx('animate-pulse rounded-card bg-inset', className)} />
);

export const EmptyState: React.FC<{ title: React.ReactNode; body?: React.ReactNode; actions?: React.ReactNode; className?: string }> = ({ title, body, actions, className }) => (
  <div className={cx('flex flex-col items-start gap-3 rounded-panel bg-card p-8', className)}>
    <div className="font-display text-card-title text-ink">{title}</div>
    {body && <p className="m-0 max-w-[60ch] text-ink-2">{body}</p>}
    {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
  </div>
);

/** A banner: bad (blocks), warn (heads-up), info, ok. */
export const Notice: React.FC<{ tone?: 'bad' | 'warn' | 'info' | 'ok'; children: React.ReactNode; action?: React.ReactNode; className?: string }> = ({
  tone = 'warn', children, action, className,
}) => {
  const Icon = tone === 'bad' ? AlertCircle : tone === 'warn' ? AlertTriangle : tone === 'ok' ? Check : Info;
  const cls = { bad: 'bg-bad-soft text-bad', warn: 'bg-warn-soft text-warn', info: 'bg-work-soft text-work', ok: 'bg-ok-soft text-ok' }[tone];
  return (
    <div role={tone === 'bad' ? 'alert' : undefined} className={cx('flex items-start gap-3 rounded-[14px] px-4 py-3 text-sm leading-normal', cls, className)}>
      <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-ink">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

/** One line of a checks list: a mark and a sentence. */
export const CheckLine: React.FC<{ level: 'ok' | 'warn' | 'block' | 'info'; children: React.ReactNode }> = ({ level, children }) => {
  const Icon = level === 'ok' ? Check : level === 'block' ? AlertCircle : level === 'warn' ? AlertTriangle : Info;
  const cls = { ok: 'text-ok', warn: 'text-warn', block: 'text-bad', info: 'text-work' }[level];
  return (
    <div className="flex items-start gap-3 border-t border-line py-3 text-sm leading-normal last:border-b">
      <Icon className={cx('mt-0.5 h-[18px] w-[18px] shrink-0', cls)} aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};
